import type { LegacyPaymentOption, PaymentOption, RoomRate } from '@niarab2c/frontend-commons/src/types/hotels';
import { NiaraIcon } from '@niaratech/niara-icons';
import { printPrice } from '@niaratech/niara-js-commons';
import { useImmutableFunction } from '@niaratech/niara-react-components/src';
import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import type { HotelPostSearchRule } from 'niara-spear-crudmodel';
import { IcCardAmex, IcCardDinners, IcCardDiscover, IcCardElo, IcCardHipercard, IcCardJcb, IcCardMaster, IcCardVisa } from '../../components/CardBrand/Brands';
import ModalOTA from '../../components/ModalOTA';
import MoreValueOptions from '../../components/MoreValueOptions';
import * as S from './styles';
export type PaymentProps = {
  paymentOptions?: LegacyPaymentOption[] | PaymentOption[];
  textSize?: number;
  textColor?: string;
  fontWeight?: number | string;
  displayMode?: 'VERTICAL' | 'HORIZONTAL' | 'BEST_OPTION' | 'SECOND_BEST_OPTION' | 'WORST_OPTION' | 'DEFAULT_OPTION' // baseado no campo "Pagamento padrao para retorno da busca" (defaultPaymentOption) da regra de Forma de Recebimento
  | 'OPPOSITE_DEFAULT_OPTION'; // faz o oposto do DEFAULT_OPTION, pegando o menor ou maior preço
  maxPaymentToDisplay?: number;
  ellipsis?: boolean;
  showStartIcon?: boolean;
  disableMorePayments?: boolean;
  justifyContent?: string;
  highlightBestPrice?: boolean;
  priceComposition?: RoomRate['priceComposition']; // necessário quando o displayMode é DEFAULT_OPTION ou OPPOSITE_DEFAULT_OPTION
  priceDisplayMode?: HotelPostSearchRule['priceDisplayMode'];
};
export const PAYMENT_TYPES = {
  DIR: "Pagamento direto",
  DIR_IATA: "Garantia IATA",
  OFF_CC: "Cartão de crédito",
  ON_CC: "Cartão de crédito",
  ON_PIX: "PIX",
  OFF_ANT: "Antecipado",
  OFF_FAT: "Faturado",
  LATER: "Pagar depois"
} as const;
type Brands = {
  cardBrand: 'MASTERCARD' | 'VISA' | 'AMEX' | 'ELO' | 'HIPER' | 'HIPERCARD' | 'AURA' | 'DINERS' | 'JCB' | 'DISCOVER';
  cardBadge?: string;
};
const CARD_BRANDS: Brands[] = [{
  cardBrand: 'MASTERCARD',
  cardBadge: IcCardMaster
}, {
  cardBrand: 'VISA',
  cardBadge: IcCardVisa
}, {
  cardBrand: 'AMEX',
  cardBadge: IcCardAmex
}, {
  cardBrand: 'ELO',
  cardBadge: IcCardElo
}, {
  cardBrand: 'HIPER',
  cardBadge: IcCardHipercard
}, {
  cardBrand: 'HIPERCARD',
  cardBadge: IcCardHipercard
}, {
  cardBrand: 'AURA',
  cardBadge: null
}, {
  cardBrand: 'DINERS',
  cardBadge: IcCardDinners
}, {
  cardBrand: 'JCB',
  cardBadge: IcCardJcb
}, {
  cardBrand: 'DISCOVER',
  cardBadge: IcCardDiscover
}];
export const icons = {
  DIR: 'htlExtDirectPaymentAtTheHotelOutlined',
  INT_DIR: 'htlIntDirectPaymentAtTheHotelOutlined',
  DIR_IATA: 'sysLanguage',
  OFF_CC: 'htlCreditCardsOutlined',
  ON_CC: 'htlCreditCardsOutlined',
  ON_PIX: 'htlPixOutlined',
  OFF_ANT: 'htlBankDepositOutlined',
  OFF_FAT: 'htlInvoicedPaymentOutlined',
  LATER: 'sysPaymentAfterOutlined'
} as const;
export const CreditCardConditions = ({
  paymentOption
}: {
  paymentOption: PaymentOption | LegacyPaymentOption;
}): React.ReactElement | null => {
  const maxCCPaymentConditions = paymentOption.oncc_conditions?.reduce((max, cur) => cur?.maxInst > (max?.maxInst || 0) ? cur : max, null);

  //Pega todas as bandeiras de cartão de crédito disponíveis e remove duplicatas
  const cardBrands = paymentOption?.oncc_conditions?.map(cc => cc.cardBrands)?.flat()?.filter(Boolean)?.filter((v, i, a) => a.indexOf(v) === i);
  if (!maxCCPaymentConditions) return null;
  const maxCCPaymentText = maxCCPaymentConditions?.maxInst > 1 ? `Pague em até ${maxCCPaymentConditions?.maxInst}x.` : null;
  const maxCCPaymentMinInstText = 'minInstValue' in maxCCPaymentConditions && maxCCPaymentConditions?.minInstValue > 0 && 'paymentCurrency' in paymentOption && `Parcela mínima de ${printPrice(maxCCPaymentConditions?.minInstValue, paymentOption?.paymentCurrency)}.`;
  return maxCCPaymentConditions && <>
        {maxCCPaymentText && <S.Text>{maxCCPaymentText}</S.Text>}
        <S.Text>{maxCCPaymentMinInstText}</S.Text>
        <S.CardIcons>
          {cardBrands?.map((card, i) => {
        const badge = CARD_BRANDS.find(brand => brand.cardBrand === card)?.cardBadge;
        return badge && <img key={i} src={badge} />;
      })}
        </S.CardIcons>
      </>;
};
const PaymentConditions: React.FC<PaymentProps> = ({
  paymentOptions,
  textSize,
  textColor,
  fontWeight,
  displayMode = 'HORIZONTAL',
  maxPaymentToDisplay = 1,
  ellipsis,
  showStartIcon,
  justifyContent,
  disableMorePayments,
  highlightBestPrice,
  priceComposition,
  priceDisplayMode
}) => {
  const [open, setOpen] = useState(false);
  const enabledPaymentOptions = React.useMemo(() => paymentOptions?.filter(po => po.type != 'LATER' && (po?.enabled || po?.enabled == null) /* #183469 considerar enabled null como habilitado */)?.sort((a, b) => {
    const aValue = 'actualTotalValue' in a ? a.actualTotalValue : 0;
    const bValue = 'actualTotalValue' in b ? b.actualTotalValue : 0;
    if (aValue != bValue) {
      return aValue - bValue;
    }
    return (a?.alias || '').localeCompare(b?.alias || '');
  }) ?? [], [paymentOptions]);
  const {
    hasAllSameValue
  } = React.useMemo(() => {
    const firstPaymentOptionValue = enabledPaymentOptions?.length > 0 && 'actualTotalValue' in enabledPaymentOptions?.[0] ? enabledPaymentOptions?.[0]?.actualTotalValue : undefined;
    const hasAllSameValue = enabledPaymentOptions?.every(option => 'actualTotalValue' in option ? option.actualTotalValue === firstPaymentOptionValue : true);
    return {
      hasAllSameValue
    };
  }, [enabledPaymentOptions]);
  const formatPaymentOptionPrice = useImmutableFunction((paymentOption: PaymentOption | LegacyPaymentOption) => {
    if (paymentOption && 'paymentValue' in paymentOption && 'paymentCurrency' in paymentOption && paymentOption?.paymentValue && paymentOption?.paymentCurrency) {
      const value = paymentOption?.priceComposition?.total?.value || paymentOption?.paymentValue;
      const currency = paymentOption?.priceComposition?.total?.currency || paymentOption?.paymentCurrency;
      return printPrice(value, currency);
    }
    return '';
  });
  const getPaymentOptionLabel = useImmutableFunction((paymentOptions: (PaymentOption | LegacyPaymentOption)[]) => {
    return paymentOptions?.map(paymentOption => {
      const includeExtraOptions = 'includeExtraOptions' in paymentOption ? paymentOption.includeExtraOptions : [];
      const extraLabel = includeExtraOptions?.length > 0 ? ' (Com extras)' : '';
      const paymentLabel = (paymentOption.alias ?? PAYMENT_TYPES[paymentOption.type]) + extraLabel;
      return paymentLabel;
    }).filter(Boolean) ?? [];
  });
  const {
    groupedPaymentOptionsByValue
  } = React.useMemo(() => {
    const paymentOptionsSortedByPrice = [...enabledPaymentOptions]?.filter(po => po.type != 'LATER')?.sort((a, b) => {
      const aActualTotalValue = 'actualTotalValue' in a ? a.actualTotalValue : 0;
      const bActualTotalValue = 'actualTotalValue' in b ? b.actualTotalValue : 0;
      return aActualTotalValue - bActualTotalValue;
    });
    const groupedPaymentOptionsByValue: Record<number, Array<(typeof paymentOptionsSortedByPrice)[number]>> = paymentOptionsSortedByPrice?.reduce((acc, paymentOption, index) => {
      const actualTotalValue = 'actualTotalValue' in paymentOption ? paymentOption?.actualTotalValue : null;
      if (!actualTotalValue) return acc;
      if (index === 0) {
        acc[0] = [paymentOption];
      } else {
        //encontra o index do acc que contenha em seus values o actualTotalValue igual no atributo actualTotalValue
        const foundIndex = Object.entries(acc).find(([, value]) => (value as (LegacyPaymentOption | PaymentOption)[])?.some(v => 'actualTotalValue' in v && v.actualTotalValue === actualTotalValue))?.[0];
        if (foundIndex) {
          acc[foundIndex].push(paymentOption);
        } else {
          const newIndex = Object.keys(acc).length;
          acc[newIndex] = [paymentOption];
        }
      }
      return acc;
    }, {});
    return {
      groupedPaymentOptionsByValue
    };
  }, [enabledPaymentOptions]);
  const renderModal = useCallback(() => {
    if (!open) return null;
    return createPortal(<ModalOTA show={open} setShow={setOpen} title="Condições e preços de pagamento" mobileVariant="full" render={() => <MoreValueOptions paymentOptions={enabledPaymentOptions} priceDisplayMode={priceDisplayMode} />} />, document.body // Renderiza o modal diretamente no body do documento
    );
  }, [enabledPaymentOptions, open, priceDisplayMode]);
  if (displayMode === 'VERTICAL' && enabledPaymentOptions?.length > 0) {
    const firstPaymentOption = Object.values(groupedPaymentOptionsByValue)?.[0]?.[0];
    const bestPrice = firstPaymentOption && 'actualTotalValue' in firstPaymentOption ? firstPaymentOption?.actualTotalValue : null;
    return <>
        <S.TypeOptions>
          {enabledPaymentOptions?.map((r, idx) => {
          const includeExtraOptions = 'includeExtraOptions' in r ? r.includeExtraOptions : [];
          const extraLabel = includeExtraOptions?.length > 0 ? <b> (Com extras)</b> : null;
          const highlight = !hasAllSameValue && 'actualTotalValue' in r ? r.actualTotalValue === bestPrice : false;
          return <S.UnicTypes key={r?.id} textColor={textColor} highlight={highlightBestPrice && highlight} onClick={() => setOpen(true)}>
                {showStartIcon && <div>
                    <NiaraIcon icon={icons[r?.type]} size={16} />
                  </div>}
                <S.TextTypePayment textSize={textSize} fontWeight={fontWeight} ellipsis={ellipsis} data-testid={`payment-option-${idx + 1}`}>
                  {r?.alias}
                  {extraLabel}
                </S.TextTypePayment>
              </S.UnicTypes>;
        })}
        </S.TypeOptions>
        {renderModal()}
      </>;
  }
  if (displayMode === 'HORIZONTAL') {
    const filteredPaymentOptions = [...enabledPaymentOptions]?.filter(po => po.type != 'LATER');
    const filteredPaymentOptionLabel = getPaymentOptionLabel(filteredPaymentOptions);
    const otherPaymentOptionsQty = Math.max(0, filteredPaymentOptions?.length - maxPaymentToDisplay);
    const formattedLabel = filteredPaymentOptionLabel?.length >= maxPaymentToDisplay ? `${filteredPaymentOptionLabel.slice(0, maxPaymentToDisplay).join(', ')}` : filteredPaymentOptionLabel?.[0];
    if (!filteredPaymentOptions || filteredPaymentOptions.length === 0) return null;
    return <>
        <S.PaymentInfo justifyContent={justifyContent} onClick={() => setOpen(true)}>
          {showStartIcon && <NiaraIcon icon={icons[filteredPaymentOptions?.[0]?.type]} size={16} color={textColor} />}
          <S.PaymentLine textSize={textSize} textColor={textColor} fontWeight={fontWeight} ellipsis={ellipsis}>
            {formattedLabel}
          </S.PaymentLine>

          {!disableMorePayments ? <>
              {otherPaymentOptionsQty === 0 && <NiaraIcon size={16} icon="sysInfo" color="#616161" />}
              {otherPaymentOptionsQty > 0 && <S.MorePayments textSize={textSize}>(+{otherPaymentOptionsQty})</S.MorePayments>}
            </> : null}
        </S.PaymentInfo>
        {renderModal()}
      </>;
  }
  if (displayMode === 'BEST_OPTION') {
    const bestPricePaymentOptions = Object.values(groupedPaymentOptionsByValue)?.length > 0 ? Object.values(groupedPaymentOptionsByValue)?.[0] : enabledPaymentOptions;
    const bestPricePaymentOptionLabel = getPaymentOptionLabel(bestPricePaymentOptions);
    const otherPaymentOptionsQty = Math.max(0, bestPricePaymentOptions?.length - maxPaymentToDisplay);
    const formattedLabel = bestPricePaymentOptionLabel?.length >= maxPaymentToDisplay ? `${bestPricePaymentOptionLabel.slice(0, maxPaymentToDisplay).join(', ')}` : bestPricePaymentOptionLabel?.[0];
    if (!bestPricePaymentOptions || bestPricePaymentOptions.length === 0) return null;
    return <>
        <S.PaymentInfo justifyContent={justifyContent} onClick={() => setOpen(true)}>
          {showStartIcon && <NiaraIcon icon={icons[bestPricePaymentOptions?.[0]?.type]} size={16} color={textColor} />}
          <S.PaymentLine textSize={textSize} textColor={textColor} fontWeight={fontWeight} ellipsis={ellipsis}>
            {formattedLabel}
          </S.PaymentLine>

          {!disableMorePayments ? <>
              {otherPaymentOptionsQty === 0 && <NiaraIcon size={16} icon="sysInfo" color="#616161" />}
              {otherPaymentOptionsQty > 0 && <S.MorePayments textSize={textSize}>(+{otherPaymentOptionsQty})</S.MorePayments>}
            </> : null}
        </S.PaymentInfo>
        {renderModal()}
      </>;
  }
  if (displayMode === 'SECOND_BEST_OPTION') {
    //precisa ter mais de um preço para poder exibir o segundo melhor
    if (Object.values(groupedPaymentOptionsByValue)?.length <= 1) return null;
    const secondBestPricePaymentOptions = Object.values(groupedPaymentOptionsByValue)?.[1];
    const secondBestPricePaymentOptionLabel = getPaymentOptionLabel(secondBestPricePaymentOptions);
    const secondBestPricePaymentOptionFormattedValue = formatPaymentOptionPrice(secondBestPricePaymentOptions?.[0]);
    const otherPaymentOptionsQty = Math.max(0, secondBestPricePaymentOptions?.length - maxPaymentToDisplay);
    const formattedLabel = secondBestPricePaymentOptionLabel?.length >= maxPaymentToDisplay ? `${secondBestPricePaymentOptionLabel.slice(0, maxPaymentToDisplay).join(', ')}` : secondBestPricePaymentOptionLabel?.[0];
    if (!secondBestPricePaymentOptions || secondBestPricePaymentOptions.length === 0) return null;
    return <>
        <S.PaymentInfo justifyContent={justifyContent} onClick={() => setOpen(true)}>
          {showStartIcon && <NiaraIcon icon={icons[secondBestPricePaymentOptions?.[0]?.type]} size={16} color={textColor} />}
          <S.PaymentLine textSize={textSize} textColor={textColor} fontWeight={fontWeight} ellipsis={ellipsis}>
            {secondBestPricePaymentOptionFormattedValue + ' ' + formattedLabel}
          </S.PaymentLine>

          {!disableMorePayments ? <>
              {otherPaymentOptionsQty === 0 && <NiaraIcon width={14} height={14} icon="sysInfo" color="#616161" />}
              {otherPaymentOptionsQty > 0 && <S.MorePayments textSize={textSize}>(+{otherPaymentOptionsQty})</S.MorePayments>}
            </> : null}
        </S.PaymentInfo>
        {renderModal()}
      </>;
  }
  if (displayMode === 'WORST_OPTION') {
    //precisa ter mais de um preço para poder exibir o pior
    if (Object.values(groupedPaymentOptionsByValue)?.length <= 1) return null;
    const worstPricePaymentOptions = Object.values(groupedPaymentOptionsByValue)?.[Object.values(groupedPaymentOptionsByValue)?.length - 1];
    const worstPricePaymentOptionLabel = getPaymentOptionLabel(worstPricePaymentOptions);
    const worstPricePaymentOptionFormattedValue = formatPaymentOptionPrice(worstPricePaymentOptions?.[0]);
    const otherPaymentOptionsQty = Math.max(0, worstPricePaymentOptions?.length - maxPaymentToDisplay);
    const formattedLabel = worstPricePaymentOptionLabel?.length >= maxPaymentToDisplay ? `${worstPricePaymentOptionLabel.slice(0, maxPaymentToDisplay).join(', ')}` : worstPricePaymentOptionLabel?.[0];
    if (!worstPricePaymentOptions || worstPricePaymentOptions?.length === 0) return null;
    return <>
        <S.PaymentInfo justifyContent={justifyContent} onClick={() => setOpen(true)}>
          {showStartIcon && <NiaraIcon icon={icons[worstPricePaymentOptions?.[0]?.type]} size={16} color={textColor} />}
          <S.PaymentLine textSize={textSize} textColor={textColor} fontWeight={fontWeight} ellipsis={ellipsis}>
            {worstPricePaymentOptionFormattedValue + ' ' + formattedLabel}
          </S.PaymentLine>

          {!disableMorePayments ? <>
              {otherPaymentOptionsQty === 0 && <NiaraIcon width={14} height={14} icon="sysInfo" color="#616161" />}
              {otherPaymentOptionsQty > 0 && <S.MorePayments textSize={textSize}>(+{otherPaymentOptionsQty})</S.MorePayments>}
            </> : null}
        </S.PaymentInfo>
        {renderModal()}
      </>;
  }
  if (displayMode === 'DEFAULT_OPTION') {
    const hasGroups = Object.values(groupedPaymentOptionsByValue)?.length <= 1 ? false : true;
    const defaultIndex = Object.values(groupedPaymentOptionsByValue)?.findIndex(paymentOptions => ('priceComposition' in paymentOptions?.[0] ? paymentOptions?.[0]?.priceComposition?.total?.value : 'actualTotalValue' in paymentOptions?.[0] ? paymentOptions?.[0]?.actualTotalValue : null) == priceComposition?.total?.value);
    const idx = defaultIndex === -1 ? 0 : defaultIndex;
    const defaultPricePaymentOptions = hasGroups ? Object.values(groupedPaymentOptionsByValue)?.[idx] : enabledPaymentOptions;
    const defaultPricePaymentOptionLabel = getPaymentOptionLabel(defaultPricePaymentOptions);
    const otherPaymentOptionsQty = Math.max(0, defaultPricePaymentOptions?.length - maxPaymentToDisplay);
    const formattedLabel = defaultPricePaymentOptionLabel?.length >= maxPaymentToDisplay ? `${defaultPricePaymentOptionLabel.slice(0, maxPaymentToDisplay).join(', ')}` : defaultPricePaymentOptionLabel?.[0];
    if (!defaultPricePaymentOptions || defaultPricePaymentOptions?.length === 0) return null;
    return <>
        <S.PaymentInfo justifyContent={justifyContent} onClick={() => setOpen(true)}>
          {showStartIcon && <NiaraIcon icon={icons[defaultPricePaymentOptions?.[0]?.type]} size={16} color={textColor} />}
          <S.PaymentLine textSize={textSize} textColor={textColor} fontWeight={fontWeight} ellipsis={ellipsis}>
            {formattedLabel}
          </S.PaymentLine>

          {!disableMorePayments ? <>
              {otherPaymentOptionsQty === 0 && <NiaraIcon width={14} height={14} icon="sysInfo" color="#616161" />}
              {otherPaymentOptionsQty > 0 && <S.MorePayments textSize={textSize}>(+{otherPaymentOptionsQty})</S.MorePayments>}
            </> : null}
        </S.PaymentInfo>
        {renderModal()}
      </>;
  }
  if (displayMode === 'OPPOSITE_DEFAULT_OPTION') {
    //precisa ter mais de um preço para poder exibir o opposite default option
    if (Object.values(groupedPaymentOptionsByValue)?.length <= 1) return null;
    const defaultIndex = Object.values(groupedPaymentOptionsByValue)?.findIndex(paymentOptions => ('priceComposition' in paymentOptions?.[0] ? paymentOptions?.[0]?.priceComposition?.total?.value : 'actualTotalValue' in paymentOptions?.[0] ? paymentOptions?.[0]?.actualTotalValue : null) == priceComposition?.total?.value);

    // se tiver mais que um preço, mostra o preço mais caro, mas se já for o mais caro, nem mostra
    const idx = defaultIndex === -1 ? null : defaultIndex >= 0 && defaultIndex < Object.values(groupedPaymentOptionsByValue)?.length - 1 ? Object.values(groupedPaymentOptionsByValue)?.length - 1 : null;
    if (idx === null) return null;
    const oppositeDefaultPricePaymentOptions = Object.values(groupedPaymentOptionsByValue)?.[idx];
    const oppositeDefaultPricePaymentOptionLabel = getPaymentOptionLabel(oppositeDefaultPricePaymentOptions);
    const oppositeDefaultPricePaymentOptionFormattedValue = formatPaymentOptionPrice(oppositeDefaultPricePaymentOptions?.[0]);
    const otherPaymentOptionsQty = Math.max(0, oppositeDefaultPricePaymentOptions?.length - maxPaymentToDisplay);
    const formattedLabel = oppositeDefaultPricePaymentOptionLabel?.length >= maxPaymentToDisplay ? `${oppositeDefaultPricePaymentOptionLabel.slice(0, maxPaymentToDisplay).join(', ')}` : oppositeDefaultPricePaymentOptionLabel?.[0];
    if (!oppositeDefaultPricePaymentOptions || oppositeDefaultPricePaymentOptions?.length === 0) return null;
    return <>
        <S.PaymentInfo justifyContent={justifyContent} onClick={() => setOpen(true)}>
          {showStartIcon && <NiaraIcon icon={icons[oppositeDefaultPricePaymentOptions?.[0]?.type]} size={16} color={textColor} />}
          <S.PaymentLine textSize={textSize} textColor={textColor} fontWeight={fontWeight} ellipsis={ellipsis}>
            {oppositeDefaultPricePaymentOptionFormattedValue + ' ' + formattedLabel}
          </S.PaymentLine>

          {!disableMorePayments ? <>
              {otherPaymentOptionsQty === 0 && <NiaraIcon width={14} height={14} icon="sysInfo" color="#616161" />}
              {otherPaymentOptionsQty > 0 && <S.MorePayments textSize={textSize}>(+{otherPaymentOptionsQty})</S.MorePayments>}
            </> : null}
        </S.PaymentInfo>
        {renderModal()}
      </>;
  }
  return null;
};
export default PaymentConditions;